import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor, useDroppable, useDraggable } from '@dnd-kit/core';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Loader2, Plus, ChevronRight, Phone, Mail, X, ArrowRight, AlertTriangle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const COLUMNS = [
  { id: 'new_lead', label: 'Discovery', color: '#6B7280', bg: '#FAFBFC' },
  { id: 'qualified', label: 'Qualificato', color: '#1D4ED8', bg: '#FAFBFC' },
  { id: 'proposal', label: 'Proposta', color: '#D97706', bg: '#FAFBFC' },
  { id: 'negotiation', label: 'Negoziazione', color: '#7C3AED', bg: '#FAFBFC' },
  { id: 'won', label: 'Chiuso Vinto', color: '#059669', bg: '#F0FDF4' },
  { id: 'lost', label: 'Chiuso Perso', color: '#DC2626', bg: '#FEF2F2' },
];

const STAGE_ALIASES = {
  new_lead: 'new_lead', contacted: 'new_lead', lead: 'new_lead',
  prospect: 'new_lead', discovery: 'new_lead',
  qualified: 'qualified', proposal: 'proposal', negotiation: 'negotiation',
  won: 'won', closed_won: 'won', lost: 'lost', closed_lost: 'lost',
};

const LOSS_REASONS = ['Prezzo', 'Tempistica', 'Concorrenza', 'Budget', 'Non interessato', 'Altro'];

function priorityColor(p) {
  switch (p) { case 'high': return '#DC2626'; case 'medium': return '#D97706'; default: return '#9CA3AF'; }
}

/* Droppable Column */
function KanbanColumn({ column, deals, onCardClick, activeId }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const total = deals.reduce((sum, d) => sum + (d.estimated_value_chf || 0), 0);

  return (
    <div ref={setNodeRef}
      className="flex-1 min-w-[200px] flex flex-col rounded-lg transition-all"
      style={{
        backgroundColor: isOver ? '#DBEAFE' : column.bg,
        border: isOver ? '2px dashed #2563EB' : '1px solid #E5E7EB',
      }}
      data-testid={`column-${column.id}`}>
      {/* Column Header */}
      <div className="px-3 py-2.5 flex items-center justify-between flex-shrink-0" style={{ borderBottom: '1px solid #E5E7EB' }}>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: column.color }} />
          <span className="text-[12px] font-semibold" style={{ color: '#1B2B4B' }}>{column.label}</span>
          <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#F3F4F6', color: '#7C8CA2' }}>
            {deals.length}
          </span>
        </div>
        <span className="text-[11px] font-medium" style={{ color: column.color }}>
          CHF {total.toLocaleString('it-CH')}
        </span>
      </div>
      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[100px]">
        {deals.map(deal => (
          <DealCard key={deal.id} deal={deal} onClick={() => onCardClick(deal)} isActive={activeId === deal.id} />
        ))}
        {deals.length === 0 && (
          <div className="text-center py-6">
            <p className="text-[11px]" style={{ color: '#9CA3AF' }}>Trascina qui</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* Draggable Deal Card */
function DealCard({ deal, onClick, isActive }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: deal.id });
  const daysInStage = deal.stage_entered_at
    ? Math.floor((Date.now() - new Date(deal.stage_entered_at).getTime()) / 86400000)
    : deal.days_in_stage || deal.created_at
      ? Math.floor((Date.now() - new Date(deal.created_at).getTime()) / 86400000)
      : null;
  const isStale = daysInStage !== null && daysInStage > 10;

  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}
      className="rounded-lg p-3 cursor-grab active:cursor-grabbing transition-shadow hover:shadow-md"
      style={{
        ...style,
        backgroundColor: '#FFFFFF',
        border: isActive ? '2px solid #2563EB' : isStale ? '1px solid #FCA5A5' : '1px solid #E5E7EB',
        boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 2px rgba(0,0,0,0.05)',
      }}
      data-testid={`deal-card-${deal.id}`}>
      <div className="flex items-start justify-between mb-1">
        <p className="text-[13px] font-semibold line-clamp-2" style={{ color: '#1B2B4B' }}>
          {deal.contact_name || deal.title || 'Deal'}
        </p>
        <span className="h-2 w-2 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: priorityColor(deal.priority) }} />
      </div>
      {deal.company_name && (
        <p className="text-[11px] truncate" style={{ color: '#7C8CA2' }}>{deal.company_name}</p>
      )}
      {(deal.product_name || deal.product) && (
        <p className="text-[11px] truncate mt-0.5" style={{ color: '#2563EB' }}>
          {deal.product_name || deal.product}
        </p>
      )}
      <div className="flex items-center justify-between mt-2">
        <span className="text-[14px] font-bold" style={{ color: '#059669' }}>
          CHF {(deal.estimated_value_chf || 0).toLocaleString('it-CH')}
        </span>
        {daysInStage !== null && (
          <div className="flex items-center gap-1">
            {isStale && <AlertTriangle className="h-3 w-3" style={{ color: '#DC2626' }} />}
            <span className="text-[10px] font-medium" style={{ color: isStale ? '#DC2626' : '#9CA3AF' }}>
              {daysInStage}g
            </span>
          </div>
        )}
      </div>
      {isStale && (
        <div className="mt-1.5 flex items-center gap-1 rounded px-1.5 py-0.5" style={{ backgroundColor: '#FEE2E2' }}>
          <span className="text-[9px] font-bold" style={{ color: '#DC2626' }}>STALLO</span>
        </div>
      )}
      {deal.next_action && (
        <p className="mt-1.5 text-[10px] truncate" style={{ color: '#6B7280' }}>
          <Clock className="inline h-3 w-3 mr-0.5" style={{ color: '#9CA3AF' }} />
          {deal.next_action}
        </p>
      )}
      <div className="flex items-center justify-end mt-1.5 gap-2">
        <button
          onClick={e => { e.stopPropagation(); onClick(); }}
          onPointerDown={e => e.stopPropagation()}
          className="text-[11px] font-medium hover:underline" style={{ color: '#2563EB' }}>
          Dettagli
        </button>
      </div>
    </div>
  );
}

/* Drag Overlay Card */
function DealOverlayCard({ deal }) {
  return (
    <div className="rounded-lg p-3 w-[200px]"
      style={{ backgroundColor: '#FFFFFF', border: '2px solid #2563EB', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
      <p className="text-[13px] font-semibold" style={{ color: '#1B2B4B' }}>
        {deal.product_name || deal.title || 'Deal'}
      </p>
      <p className="text-[11px]" style={{ color: '#7C8CA2' }}>{deal.contact_name}</p>
      <span className="text-[14px] font-bold mt-1 block" style={{ color: '#059669' }}>
        CHF {(deal.estimated_value_chf || 0).toLocaleString('it-CH')}
      </span>
    </div>
  );
}


/* ─── Main Page ─── */
export default function SalesPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [lossDialogOpen, setLossDialogOpen] = useState(false);
  const [lossReason, setLossReason] = useState('');
  const [pendingLossDealId, setPendingLossDealId] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const loadDeals = useCallback(async () => {
    setLoading(true);
    try {
      const [dealsRes, summaryRes] = await Promise.allSettled([
        api.get('/sales/opportunities'),
        api.get('/sales/pipeline-summary'),
      ]);
      if (dealsRes.status === 'fulfilled') {
        const items = (dealsRes.value?.data || []).map(d => ({
          ...d,
          column: STAGE_ALIASES[d.stage] || 'new_lead',
        }));
        setDeals(items);
      }
      if (summaryRes.status === 'fulfilled') setPipelineSummary(summaryRes.value?.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  const dealsByColumn = {};
  COLUMNS.forEach(c => { dealsByColumn[c.id] = deals.filter(d => d.column === c.id); });

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !active) return;

    const dealId = active.id;
    const newColumn = over.id;
    const deal = deals.find(d => d.id === dealId);
    if (!deal || deal.column === newColumn) return;

    // If dropping to "lost" column, show dialog
    if (newColumn === 'lost') {
      setPendingLossDealId(dealId);
      setLossDialogOpen(true);
      return;
    }

    // Optimistic update
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, column: newColumn, stage: newColumn } : d));

    try {
      await api.patch(`/sales/opportunities/${dealId}/stage`, { stage: newColumn });
      toast.success(`Deal spostato in "${COLUMNS.find(c => c.id === newColumn)?.label}"`);
    } catch {
      // Revert
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, column: deal.column, stage: deal.stage } : d));
      toast.error('Errore spostamento');
    }
  };

  const confirmLoss = async () => {
    if (!pendingLossDealId) return;
    setDeals(prev => prev.map(d => d.id === pendingLossDealId ? { ...d, column: 'lost', stage: 'lost' } : d));
    try {
      await api.patch(`/sales/opportunities/${pendingLossDealId}/stage`, {
        stage: 'lost', loss_reason: lossReason,
      });
      toast.success('Deal spostato in Chiuso Perso');
    } catch {
      loadDeals();
      toast.error('Errore');
    }
    setLossDialogOpen(false);
    setLossReason('');
    setPendingLossDealId(null);
  };

  const openDealSheet = async (deal) => {
    try {
      const res = await api.get(`/sales/opportunities/${deal.id}`);
      setSelectedDeal(res.data || deal);
    } catch { setSelectedDeal(deal); }
    setSheetOpen(true);
  };

  const activeDeal = deals.find(d => d.id === activeId);
  const totalPipeline = deals.filter(d => !['won', 'lost'].includes(d.column))
    .reduce((sum, d) => sum + (d.estimated_value_chf || 0), 0);
  const totalWon = deals.filter(d => d.column === 'won')
    .reduce((sum, d) => sum + (d.estimated_value_chf || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: '#FAFBFC' }}
        data-testid="pipeline-loading">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2563EB' }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="pipeline-page" style={{ backgroundColor: '#FAFBFC' }}>
      {/* Header */}
      <div className="px-5 py-4 flex-shrink-0" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[18px] font-bold" style={{ color: '#1B2B4B' }}>Pipeline vendite</h1>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-[14px] font-bold" style={{ color: '#1B2B4B' }}>
                Pipeline: CHF {totalPipeline.toLocaleString('it-CH')}
              </span>
              <span className="text-[13px]" style={{ color: '#7C8CA2' }}>
                {deals.length} deal attivi
              </span>
              {totalWon > 0 && (
                <span className="text-[14px] font-bold" style={{ color: '#059669' }}>
                  Vinti: CHF {totalWon.toLocaleString('it-CH')}
                </span>
              )}
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-medium text-white opacity-60 cursor-not-allowed"
                  style={{ backgroundColor: '#9CA3AF' }}
                  data-testid="new-deal-btn">
                  <Plus size={14} />Nuovo deal
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">Funzionalità in sviluppo</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Kanban Board */}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex-1 overflow-x-auto p-4">
          <div className="flex gap-3 min-w-max h-full">
            {COLUMNS.map(col => (
              <KanbanColumn key={col.id} column={col} deals={dealsByColumn[col.id] || []}
                onCardClick={openDealSheet} activeId={activeId} />
            ))}
          </div>
        </div>
        <DragOverlay>
          {activeDeal ? <DealOverlayCard deal={activeDeal} /> : null}
        </DragOverlay>
      </DndContext>

      {/* Deal Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[420px] sm:w-[420px] overflow-y-auto" data-testid="deal-sheet">
          <SheetHeader>
            <SheetTitle className="text-[16px]" style={{ color: '#1B2B4B' }}>
              {selectedDeal?.product_name || selectedDeal?.title || 'Deal'}
            </SheetTitle>
          </SheetHeader>
          {selectedDeal && (
            <div className="mt-4 space-y-5">
              {/* Value */}
              <div className="text-center py-4 rounded-lg" style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                <span className="text-[28px] font-bold" style={{ color: '#059669' }}>
                  CHF {(selectedDeal.estimated_value_chf || 0).toLocaleString('it-CH')}
                </span>
                <p className="text-[12px] mt-1" style={{ color: '#059669' }}>
                  {selectedDeal.probability_pct || 0}% probabilità
                </p>
              </div>

              {/* Contact */}
              <div className="rounded-lg p-4" style={{ border: '1px solid #E5E7EB' }}>
                <p className="text-[12px] font-medium mb-2" style={{ color: '#7C8CA2' }}>Contatto</p>
                <p className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>{selectedDeal.contact_name}</p>
                <p className="text-[12px]" style={{ color: '#7C8CA2' }}>{selectedDeal.contact_email}</p>
                <div className="flex gap-2 mt-2">
                  {selectedDeal.contact_email && (
                    <a href={`mailto:${selectedDeal.contact_email}`}
                      className="p-2 rounded-md hover:bg-gray-100 transition-colors" style={{ border: '1px solid #E5E7EB' }}>
                      <Mail size={14} style={{ color: '#7C8CA2' }} />
                    </a>
                  )}
                </div>
              </div>

              {/* Stage & Priority */}
              <div className="rounded-lg p-4" style={{ border: '1px solid #E5E7EB' }}>
                <p className="text-[12px] font-medium mb-2" style={{ color: '#7C8CA2' }}>Dettagli</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-[13px]">
                    <span style={{ color: '#7C8CA2' }}>Stage</span>
                    <span className="font-medium" style={{ color: '#1B2B4B' }}>
                      {COLUMNS.find(c => c.id === (STAGE_ALIASES[selectedDeal.stage] || selectedDeal.stage))?.label || selectedDeal.stage}
                    </span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span style={{ color: '#7C8CA2' }}>Priorità</span>
                    <span className="font-medium capitalize" style={{ color: priorityColor(selectedDeal.priority) }}>
                      {selectedDeal.priority || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span style={{ color: '#7C8CA2' }}>Fonte</span>
                    <span className="font-medium" style={{ color: '#1B2B4B' }}>{selectedDeal.source || '-'}</span>
                  </div>
                  {selectedDeal.expected_close_date && (
                    <div className="flex justify-between text-[13px]">
                      <span style={{ color: '#7C8CA2' }}>Chiusura prevista</span>
                      <span className="font-medium" style={{ color: '#1B2B4B' }}>
                        {new Date(selectedDeal.expected_close_date).toLocaleDateString('it-CH')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedDeal.notes && (
                <div className="rounded-lg p-4" style={{ border: '1px solid #E5E7EB' }}>
                  <p className="text-[12px] font-medium mb-2" style={{ color: '#7C8CA2' }}>Note</p>
                  <p className="text-[13px] whitespace-pre-wrap" style={{ color: '#4B5563' }}>{selectedDeal.notes}</p>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Loss Reason Dialog */}
      <Dialog open={lossDialogOpen} onOpenChange={setLossDialogOpen}>
        <DialogContent data-testid="loss-dialog">
          <DialogHeader>
            <DialogTitle className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>Motivo della perdita</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {LOSS_REASONS.map(r => (
              <button key={r} onClick={() => setLossReason(r)}
                className="w-full text-left px-4 py-3 rounded-lg text-[13px] font-medium transition-colors"
                style={{
                  backgroundColor: lossReason === r ? '#FEE2E2' : '#F9FAFB',
                  color: lossReason === r ? '#DC2626' : '#1B2B4B',
                  border: lossReason === r ? '1px solid #DC2626' : '1px solid #E5E7EB',
                }}
                data-testid={`loss-reason-${r}`}>
                {r}
              </button>
            ))}
            <button onClick={confirmLoss} disabled={!lossReason}
              className="w-full py-3 rounded-lg text-[14px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#DC2626' }}
              data-testid="confirm-loss-btn">
              Conferma perdita
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
